import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Box,
  Typography,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import {
  Dashboard,
  People,
  Assessment,
  BarChart,
  Input,
  ExpandLess,
  ExpandMore,
  AccountCircle,
  Settings,
  Business,
  Logout,
  Calculate
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface SidebarProps {
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, handleDrawerToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const [inputOpen, setInputOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  const drawerContent = (
    <Box sx={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Brand Section */}
      <Box sx={{
        p: 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            width: 40,
            height: 40
          }}>
            <Assessment />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              Risk Analytics
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Credit Assessment Platform
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* User Profile Section */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Avatar sx={{
            width: 32,
            height: 32,
            bgcolor: theme.palette.primary.main,
            fontSize: '0.875rem',
            fontWeight: 600
          }}>
            A
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Administrator
            </Typography>
            <Typography variant="caption" color="text.secondary">
              admin@example.com
            </Typography>
          </Box>
        </Box>
        <Chip
          label="Risk Analyst"
          size="small"
          sx={{
            backgroundColor: 'rgba(0, 172, 193, 0.1)',
            color: '#00acc1',
            fontWeight: 500,
            fontSize: '0.75rem'
          }}
        />
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        <List sx={{ p: 0 }}>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/"
              selected={isActive('/') || isActive('/dashboard')}
              sx={{
                borderRadius: 2,
                mx: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '20',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main + '30',
                  },
                  '& .MuiListItemIcon-root': {
                    color: theme.palette.primary.main,
                  }
                }
              }}
              onClick={() => isMobile && handleDrawerToggle()}
            >
              <ListItemIcon>
                <Dashboard />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItemButton>
          </ListItem>

          {/* Input Section with Submenu */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => setInputOpen(!inputOpen)}
              sx={{
                borderRadius: 2,
                mx: 1,
                mb: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)',
                }
              }}
            >
              <ListItemIcon>
                <Input />
              </ListItemIcon>
              <ListItemText primary="Input" />
              {inputOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>

          <Collapse in={inputOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItemButton
                component={Link}
                to="/input/descriptive-data-codes"
                selected={isActive('/input/descriptive-data-codes')}
                sx={{
                  pl: 4,
                  borderRadius: 2,
                  mx: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main + '20',
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main + '30',
                    },
                    '& .MuiListItemIcon-root': {
                      color: theme.palette.primary.main,
                    }
                  }
                }}
                onClick={() => isMobile && handleDrawerToggle()}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Input sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText primary="Descriptive Data Codes" primaryTypographyProps={{ variant: 'body2' }} />
              </ListItemButton>

              <ListItemButton
                component={Link}
                to="/input/master-data-input"
                selected={isActive('/input/master-data-input')}
                sx={{
                  pl: 4,
                  borderRadius: 2,
                  mx: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main + '20',
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main + '30',
                    },
                    '& .MuiListItemIcon-root': {
                      color: theme.palette.primary.main,
                    }
                  }
                }}
                onClick={() => isMobile && handleDrawerToggle()}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Business sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText primary="Master Data Input" primaryTypographyProps={{ variant: 'body2' }} />
              </ListItemButton>

              <ListItemButton
                component={Link}
                to="/input/regression-analysis"
                selected={isActive('/input/regression-analysis')}
                sx={{
                  pl: 4,
                  borderRadius: 2,
                  mx: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main + '20',
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main + '30',
                    },
                    '& .MuiListItemIcon-root': {
                      color: theme.palette.primary.main,
                    }
                  }
                }}
                onClick={() => isMobile && handleDrawerToggle()}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Assessment sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText primary="Regression Analysis" primaryTypographyProps={{ variant: 'body2' }} />
              </ListItemButton>

              <ListItemButton
                component={Link}
                to="/input/rating-components"
                selected={isActive('/input/rating-components')}
                sx={{
                  pl: 4,
                  borderRadius: 2,
                  mx: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main + '20',
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main + '30',
                    },
                    '& .MuiListItemIcon-root': {
                      color: theme.palette.primary.main,
                    }
                  }
                }}
                onClick={() => isMobile && handleDrawerToggle()}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Assessment sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText primary="Rating Components" primaryTypographyProps={{ variant: 'body2' }} />
              </ListItemButton>
            </List>
          </Collapse>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/risk-assessment"
              selected={isActive('/risk-assessment')}
              sx={{
                borderRadius: 2,
                mx: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '20',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main + '30',
                  },
                  '& .MuiListItemIcon-root': {
                    color: theme.palette.primary.main,
                  }
                }
              }}
              onClick={() => isMobile && handleDrawerToggle()}
            >
              <ListItemIcon>
                <Assessment />
              </ListItemIcon>
              <ListItemText primary="Risk Assessment" />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      {/* Footer with Logout */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            '&:hover': {
              backgroundColor: 'error.main',
              color: 'white',
              '& .MuiListItemIcon-root': {
                color: 'white',
              }
            }
          }}
        >
          <ListItemIcon>
            <Logout />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Mobile Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 280,
              border: 'none',
              boxShadow: theme.shadows[8]
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        /* Desktop Permanent Drawer */
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 280,
              border: 'none',
              boxShadow: theme.shadows[4],
              position: 'relative',
              zIndex: theme.zIndex.drawer
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
};

export default Sidebar;
